import React, { useState, useEffect, useCallback } from 'react';
import { FileText, ExternalLink, Clock, DollarSign, Package, AlertTriangle, CheckCircle, XCircle, RefreshCw, Plus, ChevronLeft, Send, Search, X, Download } from 'lucide-react';
import { apiProxyGet } from '../services/api';
import { sessionHeaders } from '../services/auth';

const STATUS_COLORS = {
  'Paid': { bg: '#e6f4ea', color: '#1e7e34' },
  'Pending': { bg: '#fff8e1', color: '#b8860b' },
  'Under Review': { bg: '#e3f2fd', color: '#1565c0' },
  'Denied': { bg: '#fde8e8', color: '#c62828' },
  'Submitted': { bg: '#f3e5f5', color: '#7b1fa2' },
  'Claim Received': { bg: '#f3e5f5', color: '#7b1fa2' },
  'Documents Pending': { bg: '#fff8e1', color: '#b8860b' },
  'Investigation Pending': { bg: '#e3f2fd', color: '#1565c0' },
  'Submitted for Payment': { bg: '#e8f5e9', color: '#2e7d32' },
  'Withdrawn': { bg: '#eee', color: '#666' },
  'Closed': { bg: '#eee', color: '#666' },
};

const STATUS_ICONS = {
  'Paid': CheckCircle,
  'Pending': Clock,
  'Under Review': FileText,
  'Denied': XCircle,
  'Submitted': AlertTriangle,
  'Claim Received': FileText,
  'Documents Pending': Clock,
  'Investigation Pending': Search,
  'Submitted for Payment': DollarSign,
  'Withdrawn': XCircle,
  'Closed': XCircle,
};

// Map UPSC status to our simplified display status
function mapUpscStatus(upscStatus) {
  const map = {
    'Claim Received': 'Under Review',
    'Documents Pending': 'Pending',
    'Investigation Pending': 'Under Review',
    'Submitted for Payment': 'Submitted',
    'Paid': 'Paid',
    'Withdrawn': 'Denied',
    'Denied': 'Denied',
    'Closed': 'Denied',
  };
  return map[upscStatus] || upscStatus || 'Submitted';
}

const LOSS_CAUSES = [
  { value: 'damage_Ext', label: 'Damaged in transit' },
  { value: 'loss_Ext', label: 'Lost package' },
  { value: 'fallout_Ext', label: 'Missing contents' },
  { value: 'delay', label: 'Late delivery / Delay' },
  { value: 'abandonment', label: 'Abandonment' },
];

function StatusBadge({ status }) {
  const displayStatus = status || 'Submitted';
  const colors = STATUS_COLORS[displayStatus] || { bg: '#eee', color: '#666' };
  const Icon = STATUS_ICONS[displayStatus] || Clock;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: colors.bg, color: colors.color }}>
      <Icon size={12} />
      {displayStatus}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatCurrency(v) {
  if (v == null || v === '') return '—';
  return '$' + parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

async function neonQuery(query, params = {}) {
  const res = await fetch('/.netlify/functions/neon', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
    body: JSON.stringify({ query, params }),
  });
  const data = await res.json();
  return data.rows || data;
}

async function insureshieldApi(action, params = {}) {
  const res = await fetch('/.netlify/functions/insureshield-proxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });
  const text = await res.text();
  try { return JSON.parse(text); }
  catch { throw new Error(`InsureShield API error (${res.status}): ${text.substring(0, 300)}`); }
}

// Generate / upload the invoice PDF UPS requires with a claim.
async function claimPackingSlip(action, params = {}) {
  const res = await fetch('/.netlify/functions/claim-packing-slip', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });
  const text = await res.text();
  let json; try { json = JSON.parse(text); } catch { throw new Error(`Invoice PDF error (${res.status}): ${text.substring(0, 200)}`); }
  if (!res.ok || json.error) throw new Error(json.error || `Invoice PDF error (${res.status})`);
  return json;
}

function b64ToBlob(b64, type) {
  const bin = atob(b64); const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

// Trigger an auto-download of a base64 PDF under an exact filename.
function downloadPdfB64(b64, filename) {
  const url = URL.createObjectURL(b64ToBlob(b64, 'application/pdf'));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// An order's Auth.net transactions (charge + refund/credit) — the same feed the
// live admin's Transactions panel uses. A refund row has cardType Refunded/Credited.
// Goes through apiProxyGet, not a bare fetch — the /api proxy is session-gated
// (it forwards with production admin rights), so the call needs the staff token.
async function fetchOrderPayments(orderId) {
  return apiProxyGet('/Orders/OrderPaymentsByOrderId', { orderId });
}

const RESHIP_NOTE = 'Shipping Amount reflects both the Reship and Original shipping charges.';

// Normalize a ZIP to the 5-digit form InsureShield reliably accepts. UPS
// billing data stores ZIP+4 as 9 bare digits ("853454136" → "85345"); a
// dashed ZIP+4 ("85345-4136" → "85345") collapses the same way.
function normZip(z) {
  const d = String(z || '').replace(/\D/g, '');
  return d.length >= 5 ? d.slice(0, 5) : String(z || '').trim();
}

// =========== NEW CLAIM MODAL ===========
export function NewClaimModal({ onClose, onSubmitted, prefillOrder }) {
  const [step, setStep] = useState(prefillOrder ? 'form' : 'search'); // search | form | submitting | done
  const [orderSearch, setOrderSearch] = useState('');
  const [claimOrders, setClaimOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(prefillOrder || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Parse M/D/YY dates with optional time (e.g. "3/5/26 6:00AM")
  const parseDateStr = (s) => {
    if (!s) return null;
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (m) {
      let yr = parseInt(m[3], 10);
      if (yr < 100) yr += 2000;
      let timeStr = s.slice(m[0].length).trim().replace(/(\d)(AM|PM)/i, '$1 $2');
      if (timeStr) {
        const d = new Date(`${m[1]}/${m[2]}/${yr} ${timeStr}`);
        if (!isNaN(d)) return d;
      }
      return new Date(yr, parseInt(m[1],10) - 1, parseInt(m[2],10));
    }
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };

  const toISO = (s) => {
    const d = parseDateStr(s);
    if (!d) return new Date().toISOString().slice(0, 10);
    return d.toISOString().slice(0, 10);
  };

  // Auto-detect late delivery for prefilled orders
  const selectedDate = parseDateStr(prefillOrder?.selected_delivery);
  const actualDate = parseDateStr(prefillOrder?.actual_delivery);
  const isLate = selectedDate && actualDate && actualDate > selectedDate;

  // Form fields. Reason always defaults to Late delivery / Delay — every claim
  // filed from the Late Packages queue is a late delivery, refund or reship
  // alike (isLate only auto-detects when we have an actual delivery date, which
  // reships don't). Staff can still change it.
  const [lossCause, setLossCause] = useState('delay');
  // Loss/delivery date, auto-derived. Prefer the actual delivery date; when it's
  // missing (many reships have no actual scan), use the SCHEDULED delivery date
  // — NOT today. Sending today for a package that shipped weeks ago puts the loss
  // date ~a month after the ship date, which UPS InsureShield rejects with a
  // generic "Error occurred while claim submission" (this was failing reship
  // claims like orders 2017621 / 2015245). Falls back to today only if neither
  // date exists. Still submitted to InsureShield + Neon, which require it.
  const [lossDate] = useState(
    prefillOrder?.actual_delivery ? toISO(prefillOrder.actual_delivery)
    : prefillOrder?.selected_delivery ? toISO(prefillOrder.selected_delivery)
    : new Date().toISOString().slice(0, 10));
  // Ship date — InsureShield requires it and rejects a loss date that isn't
  // after it. Seeded from the order below; the UPS Bills prefill carries none.
  const [shipDateInput, setShipDateInput] = useState(prefillOrder?.ship_date ? toISO(prefillOrder.ship_date) : '');
  // Merchandise is two amounts on a reship claim (original + reship), each with
  // its own line-item breakdown; a refund claim uses just the original.
  const [origMerch, setOrigMerch] = useState(prefillOrder?.order_amount ? String(parseFloat(prefillOrder.order_amount).toFixed(2)) : '');
  const [showOrigBreak, setShowOrigBreak] = useState(false);
  const [shippingAmount, setShippingAmount] = useState(prefillOrder?.shipping_cost ? String(parseFloat(prefillOrder.shipping_cost).toFixed(2)) : '');
  const [damageDescription, setDamageDescription] = useState(
    isLate ? 'Box was delivered late. Contents spoiled.' : ''
  );
  const [merchandiseDescription, setMerchandiseDescription] = useState('Perishable seafood shipment');
  const [additionalNotes, setAdditionalNotes] = useState(
    isLate ? `Late delivery: selected ${prefillOrder.selected_delivery}, actual ${prefillOrder.actual_delivery}` : ''
  );

  // Recipient (Ship To) — InsureShield rejects a claim without a postal code.
  // Seeded from the order / UPS billing data below, editable so a missing ZIP
  // can be filled in by hand.
  const [recipName, setRecipName] = useState('');
  const [recipAddress, setRecipAddress] = useState('');
  const [recipCity, setRecipCity] = useState('');
  const [recipState, setRecipState] = useState('');
  const [recipZip, setRecipZip] = useState('');
  const [recipEmail, setRecipEmail] = useState('');
  const [recipLoading, setRecipLoading] = useState(false);

  // Resolution flow: 'refund' (simple) vs 'reship' (UPS wants the reship tracking
  // #, combined shipping = original + reship, and the reshipped merchandise).
  const [resolution, setResolution] = useState('refund');
  const [reshipInfo, setReshipInfo] = useState(null);       // { order_id, bc_order_id, tracking_number, ups_billed, ship_our_cost, ... }
  const [reshipTracking, setReshipTracking] = useState('');
  const [reshipLoading, setReshipLoading] = useState(false);
  const [origSnap, setOrigSnap] = useState({ items: [], total: 0, desc: '' });    // merchandise from the claimed order
  const [originalShipCost, setOriginalShipCost] = useState(prefillOrder?.shipping_cost ? (parseFloat(prefillOrder.shipping_cost) || 0) : 0);
  // Invoice PDF (UPS requires it) — generated for preview, uploaded on submit.
  const [pdfData, setPdfData] = useState(null);   // { pdf (base64), fileName, size }
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Refund flow: the Auth.net credit transaction id (auto-pulled from the order's
  // payments) + a generated credit-receipt PDF (the 2nd doc UPS gets).
  const [refundTxnId, setRefundTxnId] = useState('');
  const [refundInfo, setRefundInfo] = useState(null);   // { amount, date, status }
  const [refundLoading, setRefundLoading] = useState(false);
  const [creditPdf, setCreditPdf] = useState(null);
  const [creditPdfLoading, setCreditPdfLoading] = useState(false);
  const [creditPdfError, setCreditPdfError] = useState(null);

  const mapItems = (rows) => {
    const items = (rows || []).map(it => {
      const qty = Number(it.quantity) || 0;
      const list = parseFloat(it.list_price) || 0;
      const unit = parseFloat(it.unit_price) || 0;
      // Always value merchandise at the price on the ORIGINAL customer order
      // (unit_price), matching the packing slip. Fall back to catalog list price
      // only when the order has no unit price (some old orders store $0).
      const price = unit || list;
      return { name: String(it.name || '').replace(/<[^>]*>/g, '').trim() || '(item)', qty, price, source: unit ? 'charged' : (list ? 'list' : 'none'), line: price * qty };
    });
    const total = items.reduce((s, it) => s + it.line, 0);
    const desc = (rows || []).map(it => { const q = Number(it.quantity) || 0; const nm = String(it.name || '').replace(/<[^>]*>/g, '').trim(); return nm ? (q > 1 ? `${q}× ${nm}` : nm) : ''; }).filter(Boolean).join('; ').slice(0, 250);
    return { items, total, desc };
  };

  // Collapsible "verify total" line-item table for a merchandise amount.
  const renderBreak = (snap, entered, show, setShow, label) => {
    if (!snap.items.length) return null;
    const sum = snap.items.reduce((s, it) => s + it.line, 0);
    const off = Math.abs(sum - (parseFloat(entered) || 0)) > 0.005;
    return (
      <div style={{ marginBottom: 12, marginTop: -4 }}>
        <span onClick={() => setShow(v => !v)}
          style={{ fontSize: 11, color: 'var(--orange)', cursor: 'pointer', fontWeight: 600 }}>
          {show ? '▾' : '▸'} Verify {label} — {snap.items.length} item{snap.items.length === 1 ? '' : 's'}, sums to {formatCurrency(sum)}
          {off && <span style={{ color: 'var(--red)' }}> (≠ amount entered {formatCurrency(parseFloat(entered) || 0)})</span>}
        </span>
        {show && (
          <table style={{ width: '100%', fontSize: 11, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--gray-200)', color: 'var(--gray-500)' }}>
                <th style={{ textAlign: 'left', padding: '3px 6px' }}>Item</th>
                <th style={{ textAlign: 'right', padding: '3px 6px' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '3px 6px' }}>Price</th>
                <th style={{ textAlign: 'right', padding: '3px 6px' }}>Line</th>
              </tr>
            </thead>
            <tbody>
              {snap.items.map((it, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '3px 6px' }}>{it.name}
                    {it.source === 'charged' && <span style={{ color: 'var(--gray-400)' }} title="No catalog list price — using the price charged"> (charged)</span>}
                    {it.source === 'none' && <span style={{ color: 'var(--red)' }}> (no price)</span>}
                  </td>
                  <td style={{ textAlign: 'right', padding: '3px 6px' }}>{it.qty}</td>
                  <td style={{ textAlign: 'right', padding: '3px 6px' }}>{formatCurrency(it.price)}</td>
                  <td style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 600 }}>{formatCurrency(it.line)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--gray-200)', fontWeight: 700 }}>
                <td colSpan={3} style={{ textAlign: 'right', padding: '3px 6px' }}>Total (pre-discount list price)</td>
                <td style={{ textAlign: 'right', padding: '3px 6px' }}>{formatCurrency(sum)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    );
  };

  // Load orders with ship_claim flag on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await neonQuery('orders_open_claims');
        setClaimOrders(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load claim orders:', e);
      }
      setLoadingOrders(false);
    })();
  }, []);

  // Resolve the recipient address whenever an order is selected: seed from
  // selectedOrder, then look the order up in Neon (for the street address),
  // falling back to the UPS billing-data receiver fields for ZIP/city/state.
  useEffect(() => {
    if (!selectedOrder) return;
    setRecipName(
      ((selectedOrder.first_name || '') + ' ' + (selectedOrder.last_name || '')).trim()
      || selectedOrder.customer_name || selectedOrder.receiver_name || ''
    );
    setRecipAddress(selectedOrder.ship_address || '');
    setRecipCity(selectedOrder.ship_city || selectedOrder.receiver_city || '');
    setRecipState(selectedOrder.ship_state || selectedOrder.receiver_state || '');
    setRecipZip(normZip(selectedOrder.ship_zip || selectedOrder.receiver_zip || ''));
    setRecipEmail(selectedOrder.email || '');
    const sd = selectedOrder.ship_date || selectedOrder.order_date;
    setShipDateInput(sd ? toISO(sd) : '');

    if (selectedOrder.ship_zip && selectedOrder.ship_address) return;
    const orderRef = selectedOrder.order_id || selectedOrder.id || selectedOrder.bc_order_id;
    if (!orderRef) return;
    let cancelled = false;
    setRecipLoading(true);
    neonQuery('order_recipient', { orderId: orderRef })
      .then(rows => {
        if (cancelled) return;
        const row = Array.isArray(rows) ? rows[0] : null;
        if (!row) return;
        const neonName = ((row.first_name || '') + ' ' + (row.last_name || '')).trim();
        setRecipName(prev => prev || neonName);
        setRecipAddress(prev => prev || row.ship_address || '');
        setRecipCity(prev => prev || row.ship_city || '');
        setRecipState(prev => prev || row.ship_state || '');
        setRecipZip(prev => prev || normZip(row.ship_zip || ''));
        setRecipEmail(prev => prev || row.email || '');
        const rsd = row.ship_date || row.order_date;
        setShipDateInput(prev => prev || (rsd ? toISO(rsd) : ''));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRecipLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOrder]);

  // Merchandise from the CLAIMED order (used directly for a refund; the reship
  // flow swaps in the reship's merchandise). Stored in a snapshot; the "apply"
  // effect below pushes the right one into the form based on the resolution.
  useEffect(() => {
    if (!selectedOrder) return;
    const orderRef = selectedOrder.order_id || selectedOrder.id;
    if (!orderRef) return;
    let cancelled = false;
    neonQuery('order_items', { orderId: orderRef })
      .then(rows => {
        if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
        setOrigSnap(mapItems(rows));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedOrder]);

  // Find the reship for this order (if any). When one exists, default the
  // resolution to "reship" and seed its tracking #.
  useEffect(() => {
    if (!selectedOrder) return;
    const orderRef = selectedOrder.order_id || selectedOrder.id;
    if (!orderRef && !selectedOrder.bc_order_id) return;
    let cancelled = false;
    setReshipLoading(true);
    neonQuery('claim_reship_info', { orderId: orderRef || '', bcOrderId: selectedOrder.bc_order_id || '' })
      .then(rows => {
        if (cancelled) return;
        const r = Array.isArray(rows) ? rows[0] : null;
        if (r && r.order_id) { setReshipInfo(r); setReshipTracking(r.tracking_number || ''); setResolution('reship'); }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setReshipLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOrder]);

  // Seed each merchandise field from its order's line items.
  useEffect(() => { if (origSnap.total > 0) setOrigMerch(origSnap.total.toFixed(2)); }, [origSnap]);

  // Apply the resolution: merchandise description always from the ORIGINAL order
  // (we claim its merchandise). Shipping Amount = original (+ reship's UPS cost).
  useEffect(() => {
    const useReship = resolution === 'reship' && !!reshipInfo;
    if (origSnap.desc) setMerchandiseDescription(origSnap.desc);
    const reshipCost = useReship ? (parseFloat(reshipInfo.ups_billed) || parseFloat(reshipInfo.ship_our_cost) || 0) : 0;
    const combined = (originalShipCost || 0) + reshipCost;
    if (combined > 0) setShippingAmount(combined.toFixed(2));
  }, [resolution, origSnap, reshipInfo, originalShipCost]);

  // Keep the reship explainer sentence in Additional Notes in sync with the toggle.
  useEffect(() => {
    setAdditionalNotes(prev => {
      const base = (prev || '').split(RESHIP_NOTE).join('').replace(/\s{2,}/g, ' ').trim();
      if (!(resolution === 'reship' && reshipInfo)) return base;
      const sep = base ? (/[.!?]$/.test(base) ? ' ' : '. ') : '';
      return base + sep + RESHIP_NOTE;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution, reshipInfo]);

  // Generate the invoice PDF for preview — always the ORIGINAL shipment (the
  // claimed order), even on a reship claim (UPS wants the original invoice, not
  // the reship's). Uploaded to the claim on submit.
  const pdfOrderId = selectedOrder?.order_id || selectedOrder?.id || '';
  const pdfBcId = selectedOrder?.bc_order_id || '';
  useEffect(() => {
    if (!pdfOrderId) { setPdfData(null); return; }
    let cancelled = false;
    setPdfLoading(true); setPdfError(null);
    claimPackingSlip('generate', { orderId: pdfOrderId })
      .then(res => { if (!cancelled) setPdfData(res); })
      .catch(e => { if (!cancelled) { setPdfError(e.message); setPdfData(null); } })
      .finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => { cancelled = true; };
  }, [pdfOrderId]);

  function viewPdf() {
    if (!pdfData?.pdf) return;
    const url = URL.createObjectURL(b64ToBlob(pdfData.pdf, 'application/pdf'));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  // Packing slip → auto-download as "<order#>PS.pdf" (e.g. 2015442PS.pdf).
  function downloadPdf() {
    if (!pdfData?.pdf) return;
    downloadPdfB64(pdfData.pdf, `${pdfBcId || pdfOrderId}PS.pdf`);
  }

  // Refund flow: auto-pull the Auth.net credit/refund transaction for this order.
  useEffect(() => {
    if (resolution !== 'refund') return;
    const orderRef = selectedOrder?.order_id || selectedOrder?.id;
    if (!orderRef) return;
    let cancelled = false;
    setRefundLoading(true);
    fetchOrderPayments(orderRef)
      .then(rows => {
        if (cancelled || !Array.isArray(rows)) return;
        const credit = rows.find(r => /refund|credit/i.test(String(r.cardType || '')) || /#(refund|credit)/i.test(String(r.transactionId || '')));
        if (credit) {
          setRefundTxnId(String(credit.transactionId || '').split('#')[0]);
          setRefundInfo({ amount: credit.amount, date: credit.date, status: credit.cardType });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRefundLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOrder, resolution]);

  // Refund flow: generate the credit-receipt PDF for preview.
  useEffect(() => {
    if (resolution !== 'refund' || !refundTxnId || !pdfOrderId) { setCreditPdf(null); return; }
    let cancelled = false;
    setCreditPdfLoading(true); setCreditPdfError(null);
    claimPackingSlip('generate-credit', { orderId: pdfOrderId, transactionId: refundTxnId })
      .then(res => { if (!cancelled) setCreditPdf(res); })
      .catch(e => { if (!cancelled) { setCreditPdfError(e.message); setCreditPdf(null); } })
      .finally(() => { if (!cancelled) setCreditPdfLoading(false); });
    return () => { cancelled = true; };
  }, [resolution, refundTxnId, pdfOrderId]);

  function viewCreditPdf() {
    if (!creditPdf?.pdf) return;
    const url = URL.createObjectURL(b64ToBlob(creditPdf.pdf, 'application/pdf'));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  // Authorize.net credit receipt → auto-download as "<order#>AR.pdf".
  function downloadCreditPdf() {
    if (!creditPdf?.pdf) return;
    downloadPdfB64(creditPdf.pdf, `${pdfBcId || pdfOrderId}AR.pdf`);
  }

  const filteredOrders = claimOrders.filter(o => {
    if (!orderSearch) return true;
    const q = orderSearch.toLowerCase();
    return (
      String(o.bc_order_id || '').includes(q) ||
      String(o.id || '').includes(q) ||
      (o.tracking_number || '').toLowerCase().includes(q) ||
      ((o.first_name || '') + ' ' + (o.last_name || '')).toLowerCase().includes(q)
    );
  });

  function selectOrder(order) {
    setSelectedOrder(order);
    setOrigMerch(order.order_amount ? parseFloat(order.order_amount).toFixed(2) : '');
    setStep('form');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedOrder) return;
    setSubmitting(true);
    setError(null);

    try {
      // 1. Submit to InsureShield API
      // Ship date must be present and strictly before the loss date — UPS
      // rejects the claim otherwise. Both are YYYY-MM-DD, so string compare.
      const shipDate = shipDateInput;
      if (!shipDate) {
        throw new Error('A ship date is required. Enter it above (the date the package shipped).');
      }
      if (lossDate && shipDate >= lossDate) {
        throw new Error('The ship date must be before the delivery/loss date. Check the Ship Date above — it looks later than the order\'s delivery date.');
      }

      // Recipient address comes from the editable Ship To fields (seeded from
      // the order / UPS billing data). UPS rejects the claim without a ZIP.
      if (!recipZip.trim()) {
        throw new Error('A delivery ZIP code is required. Enter it in the Ship To section above — UPS rejects the claim without it.');
      }

      // Merchandise total = the ORIGINAL order's merchandise only (matches the
      // packing slip). Reship shipping is still folded into Shipping Amount.
      const totalMerch = parseFloat(origMerch) || 0;

      // On a refund, note the Auth.net credit transaction for UPS (the credit PDF
      // is the proof; this puts the id in text too).
      const notesToSend = (resolution === 'refund' && refundTxnId.trim())
        ? `${additionalNotes ? additionalNotes.trim() + ' ' : ''}Customer refunded via Authorize.net transaction ${refundTxnId.trim()}.`
        : additionalNotes;

      const apiResult = await insureshieldApi('create-claim', {
        trackingNumber: selectedOrder.tracking_number || 'UNKNOWN',
        shipDate,
        lossDate,
        lossCause,
        merchandiseAmount: totalMerch,
        shippingAmount: parseFloat(shippingAmount) || 0,
        merchandiseDescription,
        referenceNumber: String(selectedOrder.bc_order_id || ''),  // → portal "Reference #"
        damageDescription: lossCause === 'damage_Ext' ? damageDescription : undefined,
        additionalNotes: notesToSend,
        commodity: 'perishables_fruits',
        carrier: 'UPS',
        recipientName: recipName.trim(),
        recipientAddress: recipAddress.trim(),
        recipientCity: recipCity.trim(),
        recipientState: recipState.trim(),
        recipientPostalCode: normZip(recipZip),
        contactEmail: recipEmail.trim() || 'claims@crabplace.com',
        // Reship resolution: UPS wants the reship tracking #.
        reshipTrackingNumber: resolution === 'reship' ? (reshipTracking.trim() || undefined) : undefined,
      });

      const claimNumber = apiResult?.claimNumber;
      if (!claimNumber) {
        // Surface UPS's FULL rejection (code + message + raw) — InsureShield's
        // top-line text is often just "Error occurred while claim submission",
        // so include everything so we can see the actual reason.
        const errs = apiResult?.errors || apiResult?.response?.errors || [];
        const parts = (Array.isArray(errs) ? errs : [errs])
          .map(e => [e?.errorCode || e?.code, e?.errorMessage || e?.message || e?.errorDescription].filter(Boolean).join(': '))
          .filter(Boolean);
        const detail = parts.join(' | ') || apiResult?.error || apiResult?.message || '';
        throw new Error(`UPS rejected the claim${detail ? ': ' + detail : ''}. Full response: ${JSON.stringify(apiResult).substring(0, 700)}`);
      }

      // 2. Save to Neon
      const claimAmount = totalMerch + (parseFloat(shippingAmount) || 0);
      await neonQuery('claims_create', {
        claim_number: claimNumber,
        order_id: String(selectedOrder.order_id || selectedOrder.id || ''),
        bc_order_id: String(selectedOrder.bc_order_id || ''),
        tracking_number: selectedOrder.tracking_number || '',
        customer_name: recipName.trim(),
        customer_email: recipEmail.trim() || '',
        ship_date: shipDate,
        loss_date: lossDate,
        loss_cause: lossCause,
        merchandise_description: merchandiseDescription,
        merchandise_amount: totalMerch,
        shipping_amount: parseFloat(shippingAmount) || 0,
        claim_amount: claimAmount,
        status: 'Submitted',
        upsc_status: 'Claim Received',
        damage_description: damageDescription || null,
        additional_notes: notesToSend || null,
        reship_tracking_number: resolution === 'reship' ? (reshipTracking.trim() || null) : null,
      });

      // 3. Upload the invoice PDF to the claim (UPS requires it). Non-fatal —
      // the claim is already filed; surface a note if the upload fails.
      let pdfUploadError = null;
      if (pdfOrderId) {
        try { await claimPackingSlip('upload', { orderId: pdfOrderId, claimNumber }); }
        catch (e) { pdfUploadError = e.message; }
      } else {
        pdfUploadError = 'No invoice PDF was generated.';
      }

      // 4. On a refund, also upload the Auth.net credit-receipt PDF (the 2nd doc).
      if (resolution === 'refund' && refundTxnId.trim()) {
        try { await claimPackingSlip('upload-credit', { orderId: pdfOrderId, transactionId: refundTxnId.trim(), claimNumber }); }
        catch (e) { pdfUploadError = (pdfUploadError ? pdfUploadError + '; ' : '') + 'credit receipt: ' + e.message; }
      }

      setResult({ claimNumber, pdfUploadError });
      setStep('done');
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
        {step === 'done' ? (
          <>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CheckCircle size={48} color="#1e7e34" style={{ marginBottom: 12 }} />
              <h3 style={{ color: 'var(--navy)', margin: '0 0 8px' }}>Claim Filed Successfully</h3>
              <p style={{ fontSize: 14, color: 'var(--gray-500)' }}>
                Claim Number: <strong style={{ color: 'var(--navy)' }}>{result?.claimNumber}</strong>
              </p>
              <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                UPS Capital will review and you can track the status here.
              </p>
              {result?.pdfUploadError ? (
                <p style={{ fontSize: 12, color: '#b26a00', background: '#fff6e6', padding: '8px 12px', borderRadius: 6, maxWidth: 420, margin: '8px auto 0' }}>
                  Heads up: the claim filed, but the invoice PDF didn't upload ({result.pdfUploadError}). Upload it manually from the claim detail (UPS requires the invoice).
                </p>
              ) : (
                <p style={{ fontSize: 12, color: '#1a8a4a' }}>Invoice PDF uploaded to the claim.</p>
              )}
              <button className="btn btn-primary" style={{ marginTop: 16 }}
                onClick={() => { onSubmitted(); onClose(); }}>
                Done
              </button>
            </div>
          </>
        ) : step === 'search' ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: 'var(--navy)' }}>File New Claim</h3>
              <button className="btn btn-ghost" onClick={onClose} style={{ padding: 4 }}><X size={18} /></button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 }}>
              Select an order flagged for claim. These are orders with the ship_claim flag set in the system.
            </p>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--gray-400)' }} />
              <input className="form-input" placeholder="Search by order #, tracking, or customer..."
                value={orderSearch} onChange={e => setOrderSearch(e.target.value)}
                style={{ paddingLeft: 30, width: '100%' }} />
            </div>
            {loadingOrders ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--gray-500)' }}>Loading orders...</div>
            ) : filteredOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--gray-500)' }}>
                {claimOrders.length === 0 ? 'No orders flagged for claims.' : 'No matching orders.'}
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                <table className="product-table" style={{ width: '100%', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Tracking</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.slice(0, 50).map(o => (
                      <tr key={o.id}>
                        <td style={{ fontWeight: 600 }}>#{o.bc_order_id || o.id}</td>
                        <td>{(o.first_name || '') + ' ' + (o.last_name || '')}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{o.tracking_number || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(o.order_amount)}</td>
                        <td>
                          <button className="btn btn-primary" style={{ fontSize: 11, padding: '3px 10px' }}
                            onClick={() => selectOrder(o)}>Select</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Manual entry option */}
            <div style={{ borderTop: '1px solid #eee', marginTop: 16, paddingTop: 12, textAlign: 'center' }}>
              <button className="btn btn-outline" style={{ fontSize: 12 }}
                onClick={() => { setSelectedOrder({ id: '', bc_order_id: '', tracking_number: '', first_name: '', last_name: '', email: '', order_amount: 0, order_date: new Date().toISOString() }); setStep('form'); }}>
                Enter claim details manually
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Sticky header so the X stays reachable while scrolling the form.
                Negative margins cancel the modal's 24px padding so the bar spans
                full width and pins flush to the top. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 5, background: '#fff', margin: '-24px -24px 16px -24px', padding: '18px 24px 12px 24px', borderBottom: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setStep('search')}>
                  <ChevronLeft size={18} />
                </button>
                <h3 style={{ margin: 0, color: 'var(--navy)' }}>Claim Details</h3>
              </div>
              <button className="btn btn-ghost" onClick={onClose} style={{ padding: 4 }}><X size={18} /></button>
            </div>

            {selectedOrder?.bc_order_id && (
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                <strong>Order #{selectedOrder.bc_order_id || selectedOrder.id}</strong>
                {' — '}{(selectedOrder.first_name || '') + ' ' + (selectedOrder.last_name || '')}
                {selectedOrder.tracking_number && <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>{selectedOrder.tracking_number}</span>}
              </div>
            )}

            {error && (
              <div style={{ background: '#fde8e8', color: '#c62828', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Reason for Claim</label>
                <select className="form-select" value={lossCause} onChange={e => setLossCause(e.target.value)}>
                  {LOSS_CAUSES.map(lc => <option key={lc.value} value={lc.value}>{lc.label}</option>)}
                </select>
              </div>

              {/* Resolution — refund vs reship. The reship flow submits the reship
                  tracking #, the reshipped merchandise, and combined shipping. */}
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  How was this resolved?
                  {reshipLoading && <RefreshCw size={11} className="spin" />}
                </label>
                <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
                  {[{ k: 'refund', label: 'Refund' }, { k: 'reship', label: 'Reship' }].map(opt => (
                    <button type="button" key={opt.k} onClick={() => setResolution(opt.k)}
                      style={{
                        padding: '6px 18px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                        background: resolution === opt.k ? 'var(--navy)' : '#fff',
                        color: resolution === opt.k ? '#fff' : 'var(--navy)',
                      }}>{opt.label}</button>
                  ))}
                </div>
                {reshipInfo && resolution === 'refund' && (
                  <div style={{ fontSize: 11, color: 'var(--orange)', marginTop: 6 }}>
                    This order was reshipped (Order #{reshipInfo.bc_order_id}). If the resolution was a reship, switch to "Reship".
                  </div>
                )}
              </div>

              {resolution === 'reship' && (
                <div style={{ background: '#f3f7fb', border: '1px solid #d6e3ef', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
                    Reship details
                  </div>
                  {reshipInfo ? (
                    <div style={{ fontSize: 12.5, color: 'var(--navy)', marginBottom: 8 }}>
                      Reshipped as <strong>Order #{reshipInfo.bc_order_id}</strong>
                      {(reshipInfo.first_name || reshipInfo.last_name) ? ` — ${(reshipInfo.first_name || '') + ' ' + (reshipInfo.last_name || '')}`.trimEnd() : ''}
                      {' · '}reship UPS cost {formatCurrency(parseFloat(reshipInfo.ups_billed) || parseFloat(reshipInfo.ship_our_cost) || 0)}
                      {!reshipInfo.ups_billed && <span style={{ color: 'var(--gray-500)' }}> (scale estimate — not yet billed)</span>}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
                      No linked reship order found — enter the reship tracking # by hand.
                    </div>
                  )}
                  <label className="form-label" style={{ fontSize: 12 }}>Reship Tracking Number</label>
                  <input className="form-input" value={reshipTracking}
                    onChange={e => setReshipTracking(e.target.value)} placeholder="1Z…" style={{ fontFamily: 'monospace' }} />
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                    Merchandise = the reshipped goods. Shipping Amount below = original
                    {originalShipCost ? ` (${formatCurrency(originalShipCost)})` : ''} + reship, with a note to UPS that it covers both.
                  </div>
                </div>
              )}

              {resolution === 'refund' && (
                <div style={{ background: '#f3f7fb', border: '1px solid #d6e3ef', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Refund — proof of credit
                    {refundLoading && <RefreshCw size={11} className="spin" />}
                  </div>
                  {refundInfo ? (
                    <div style={{ fontSize: 12.5, color: 'var(--navy)', marginBottom: 8 }}>
                      Found a <strong>{refundInfo.status}</strong> transaction
                      {refundInfo.amount ? ` for ${formatCurrency(refundInfo.amount)}` : ''}
                      {refundInfo.date ? ` on ${String(refundInfo.date).replace('T', ' ').slice(0, 10)}` : ''} (auto-pulled from Authorize.net).
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
                      No credit/refund transaction found on this order — paste the Authorize.net transaction ID for the refund below.
                    </div>
                  )}
                  <label className="form-label" style={{ fontSize: 12 }}>Refund Transaction ID (Authorize.net)</label>
                  <input className="form-input" value={refundTxnId}
                    onChange={e => setRefundTxnId(e.target.value)} placeholder="e.g. 81642526323" style={{ fontFamily: 'monospace' }} />
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                    We pull this transaction's details from Authorize.net and attach a credit-receipt PDF to the claim (UPS gets the invoice + this credit proof).
                  </div>
                </div>
              )}

              {/* Loss Date is no longer collected here — it's still submitted to
                  InsureShield/Neon, auto-derived from the order's delivery date
                  (see the lossDate state initializer). */}
              <div style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Ship Date</label>
                  <input className="form-input" type="date" value={shipDateInput}
                    onChange={e => setShipDateInput(e.target.value)} max={lossDate || undefined} required />
                </div>
              </div>

              {resolution === 'reship' && reshipInfo ? (
                <>
                  {/* Reship claim: claim the ORIGINAL order's merchandise only
                      (matches the packing slip total). Shipping stays combined. */}
                  <div className="form-group" style={{ marginBottom: 6 }}>
                    <label className="form-label">Merchandise Amount ($)</label>
                    <input className="form-input" type="number" step="0.01" value={origMerch}
                      onChange={e => setOrigMerch(e.target.value)} required placeholder="0.00" />
                  </div>
                  {renderBreak(origSnap, origMerch, showOrigBreak, setShowOrigBreak, 'merchandise total')}
                  <div className="form-group" style={{ marginBottom: 12, marginTop: 12 }}>
                    <label className="form-label">Shipping Amount ($) — original + reship</label>
                    <input className="form-input" type="number" step="0.01" value={shippingAmount}
                      onChange={e => setShippingAmount(e.target.value)} placeholder="0.00" />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div className="form-group">
                      <label className="form-label">Merchandise Amount ($)</label>
                      <input className="form-input" type="number" step="0.01" value={origMerch}
                        onChange={e => setOrigMerch(e.target.value)} required placeholder="0.00" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Shipping Amount ($)</label>
                      <input className="form-input" type="number" step="0.01" value={shippingAmount}
                        onChange={e => setShippingAmount(e.target.value)} placeholder="0.00" />
                    </div>
                  </div>
                  {renderBreak(origSnap, origMerch, showOrigBreak, setShowOrigBreak, 'merchandise total')}
                </>
              )}

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Merchandise Description</label>
                <input className="form-input" value={merchandiseDescription}
                  onChange={e => setMerchandiseDescription(e.target.value)} required />
              </div>

              {lossCause === 'damage_Ext' && (
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">Damage Description</label>
                  <textarea className="form-textarea" rows={2} value={damageDescription}
                    onChange={e => setDamageDescription(e.target.value)}
                    placeholder="Describe the damage..." />
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Ship To (Recipient)
                  {recipLoading && <RefreshCw size={11} className="spin" />}
                </label>
                <input className="form-input" style={{ marginBottom: 8 }} placeholder="Recipient name"
                  value={recipName} onChange={e => setRecipName(e.target.value)} />
                <input className="form-input" style={{ marginBottom: 8 }} placeholder="Street address"
                  value={recipAddress} onChange={e => setRecipAddress(e.target.value)} />
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                  <input className="form-input" placeholder="City"
                    value={recipCity} onChange={e => setRecipCity(e.target.value)} />
                  <input className="form-input" placeholder="State"
                    value={recipState} onChange={e => setRecipState(e.target.value)} />
                  <input className="form-input" placeholder="ZIP"
                    value={recipZip} onChange={e => setRecipZip(e.target.value)} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                  Pre-filled from the order and UPS billing data. UPS rejects the claim without a delivery ZIP — edit if anything is missing.
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Additional Notes</label>
                <textarea className="form-textarea" rows={2} value={additionalNotes}
                  onChange={e => setAdditionalNotes(e.target.value)}
                  placeholder="Any additional information for the claims adjuster..." />
              </div>

              {/* Invoice PDF — UPS requires it. Generated here for verification;
                  uploaded to the claim automatically on submit. */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Invoice (PDF) — required by UPS</label>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  border: '1px solid var(--gray-300)', borderRadius: 8, fontSize: 12.5,
                  background: pdfError ? '#fdf3f3' : '#f8fafc',
                }}>
                  <FileText size={18} style={{ color: pdfData ? '#1a8a4a' : 'var(--gray-400)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    {pdfLoading ? (
                      <span style={{ color: 'var(--gray-500)' }}><RefreshCw size={11} className="spin" style={{ verticalAlign: 'middle', marginRight: 4 }} />Generating invoice PDF…</span>
                    ) : pdfError ? (
                      <span style={{ color: 'var(--red)' }}>Couldn't generate the invoice PDF: {pdfError}</span>
                    ) : pdfData ? (
                      <>
                        <strong style={{ color: 'var(--navy)' }}>{pdfData.fileName}</strong>
                        <span style={{ color: 'var(--gray-500)' }}> · {(pdfData.size / 1024).toFixed(0)} KB · </span>
                        <span style={{ color: '#1a8a4a', fontWeight: 600 }}>ready ✓</span>
                        {pdfBcId ? <span style={{ color: 'var(--gray-500)' }}> · from Order #{pdfBcId}</span> : null}
                      </>
                    ) : (
                      <span style={{ color: 'var(--gray-500)' }}>No invoice generated.</span>
                    )}
                  </div>
                  {pdfData && !pdfLoading && (
                    <>
                      <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '3px 10px' }} onClick={viewPdf}>View</button>
                      <button type="button" className="btn btn-primary" style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }} onClick={downloadPdf} title={`Download ${pdfBcId || pdfOrderId}PS.pdf`}><Download size={12} />Download</button>
                    </>
                  )}
                  {(pdfError || (!pdfData && !pdfLoading)) && pdfOrderId && (
                    <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '3px 10px' }}
                      onClick={() => { setPdfData(null); setPdfError(null); setPdfLoading(true);
                        claimPackingSlip('generate', { orderId: pdfOrderId })
                          .then(setPdfData).catch(e => setPdfError(e.message)).finally(() => setPdfLoading(false)); }}>
                      Retry
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                  Uploaded to the claim automatically when you submit.
                </div>
              </div>

              {/* Refund flow: the 2nd document — the Auth.net credit receipt. */}
              {resolution === 'refund' && refundTxnId && (
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Authorize.net credit receipt (PDF)</label>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    border: '1px solid var(--gray-300)', borderRadius: 8, fontSize: 12.5,
                    background: creditPdfError ? '#fdf3f3' : '#f8fafc',
                  }}>
                    <FileText size={18} style={{ color: creditPdf ? '#1a8a4a' : 'var(--gray-400)', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      {creditPdfLoading ? (
                        <span style={{ color: 'var(--gray-500)' }}><RefreshCw size={11} className="spin" style={{ verticalAlign: 'middle', marginRight: 4 }} />Generating credit receipt…</span>
                      ) : creditPdfError ? (
                        <span style={{ color: 'var(--red)' }}>Couldn't generate the credit receipt: {creditPdfError}</span>
                      ) : creditPdf ? (
                        <>
                          <strong style={{ color: 'var(--navy)' }}>{creditPdf.fileName}</strong>
                          <span style={{ color: 'var(--gray-500)' }}> · {(creditPdf.size / 1024).toFixed(0)} KB · </span>
                          <span style={{ color: '#1a8a4a', fontWeight: 600 }}>ready ✓</span>
                        </>
                      ) : (
                        <span style={{ color: 'var(--gray-500)' }}>No credit receipt yet.</span>
                      )}
                    </div>
                    {creditPdf && !creditPdfLoading && (
                      <>
                        <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '3px 10px' }} onClick={viewCreditPdf}>View</button>
                        <button type="button" className="btn btn-primary" style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }} onClick={downloadCreditPdf} title={`Download ${pdfBcId || pdfOrderId}AR.pdf`}><Download size={12} />Download</button>
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                    Built from Authorize.net transaction {refundTxnId}. Uploaded to the claim with the invoice on submit.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {submitting ? <RefreshCw size={14} className="spin" /> : <Send size={14} />}
                  {submitting ? 'Submitting...' : 'Submit Claim to UPS Capital'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// =========== CLAIM DETAIL VIEW ===========
function ClaimDetail({ claim, onBack, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [statusData, setStatusData] = useState(null);

  async function refreshStatus() {
    if (!claim.claim_number) return;
    setRefreshing(true);
    try {
      const data = await insureshieldApi('claim-status', { claimNumber: claim.claim_number });
      setStatusData(data);

      // Update in Neon
      const upscStatus = data?.customerDisplayStatus;
      if (upscStatus) {
        const displayStatus = mapUpscStatus(upscStatus);
        const paidAmount = data?.checks?.reduce((sum, c) => sum + (c?.grossAmount?.amount || 0), 0) || null;
        const resolvedDate = ['Paid', 'Denied', 'Closed', 'Withdrawn'].includes(upscStatus)
          ? new Date().toISOString() : null;

        await neonQuery('claims_update_status', {
          claim_number: claim.claim_number,
          status: displayStatus,
          upsc_status: upscStatus,
          paid_amount: paidAmount,
          resolved_date: resolvedDate,
        });
      }
      onRefresh();
    } catch (e) {
      console.error('Status refresh failed:', e);
    }
    setRefreshing(false);
  }

  async function loadMessages() {
    if (!claim.claim_number) return;
    setLoadingMessages(true);
    try {
      const idsResult = await insureshieldApi('get-message-ids', { claimNumber: claim.claim_number });
      if (idsResult?.messageIDs?.length) {
        const detailsResult = await insureshieldApi('get-message-details', {
          claimNumber: claim.claim_number,
          messageIDs: idsResult.messageIDs.slice(0, 20),
        });
        setMessages(detailsResult?.messages || []);
      }
    } catch (e) {
      console.error('Failed to load messages:', e);
    }
    setLoadingMessages(false);
  }

  async function handleSendMessage(e) {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await insureshieldApi('create-message', {
        claimNumber: claim.claim_number,
        messageBody: newMessage.trim(),
        author: 'CrabPlace Claims',
      });
      setNewMessage('');
      await loadMessages();
    } catch (e) {
      console.error('Failed to send message:', e);
    }
    setSendingMessage(false);
  }

  useEffect(() => {
    if (claim.claim_number) loadMessages();
  }, [claim.claim_number]);

  const lossCauseLabel = LOSS_CAUSES.find(lc => lc.value === claim.loss_cause)?.label || claim.loss_cause || '—';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-ghost" style={{ padding: 4 }} onClick={onBack}><ChevronLeft size={18} /></button>
        <h3 style={{ margin: 0, color: 'var(--navy)' }}>Claim {claim.claim_number}</h3>
        <StatusBadge status={claim.upsc_status || claim.status} />
        <div style={{ flex: 1 }} />
        <button className="btn btn-outline" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={refreshStatus} disabled={refreshing}>
          <RefreshCw size={13} className={refreshing ? 'spin' : ''} />
          {refreshing ? 'Checking...' : 'Refresh Status'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Claim Info */}
        <div className="card">
          <div className="card-header"><h4 style={{ margin: 0, fontSize: 14 }}>Claim Information</h4></div>
          <div className="card-body" style={{ fontSize: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px' }}>
              <span style={{ color: 'var(--gray-500)' }}>Claim #</span><span style={{ fontWeight: 600 }}>{claim.claim_number}</span>
              <span style={{ color: 'var(--gray-500)' }}>Order</span><span>{claim.bc_order_id || claim.order_id ? `#${claim.bc_order_id || claim.order_id}` : '—'}</span>
              <span style={{ color: 'var(--gray-500)' }}>Tracking</span><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{claim.tracking_number || '—'}</span>
              <span style={{ color: 'var(--gray-500)' }}>Customer</span><span>{claim.customer_name || '—'}</span>
              <span style={{ color: 'var(--gray-500)' }}>Reason</span><span>{lossCauseLabel}</span>
              <span style={{ color: 'var(--gray-500)' }}>Filed</span><span>{formatDate(claim.filed_date)}</span>
              <span style={{ color: 'var(--gray-500)' }}>Resolved</span><span>{formatDate(claim.resolved_date)}</span>
            </div>
          </div>
        </div>

        {/* Financial */}
        <div className="card">
          <div className="card-header"><h4 style={{ margin: 0, fontSize: 14 }}>Financial Details</h4></div>
          <div className="card-body" style={{ fontSize: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px' }}>
              <span style={{ color: 'var(--gray-500)' }}>Merchandise</span><span style={{ fontWeight: 600 }}>{formatCurrency(claim.merchandise_amount)}</span>
              <span style={{ color: 'var(--gray-500)' }}>Shipping</span><span>{formatCurrency(claim.shipping_amount)}</span>
              <span style={{ color: 'var(--gray-500)' }}>Total Claimed</span><span style={{ fontWeight: 700, color: 'var(--navy)' }}>{formatCurrency(claim.claim_amount)}</span>
              <span style={{ color: 'var(--gray-500)' }}>Paid</span>
              <span style={{ fontWeight: 700, color: claim.paid_amount ? '#1e7e34' : 'var(--gray-400)' }}>
                {formatCurrency(claim.paid_amount)}
              </span>
              <span style={{ color: 'var(--gray-500)' }}>UPSC Status</span><span>{claim.upsc_status || '—'}</span>
              <span style={{ color: 'var(--gray-500)' }}>Last Checked</span><span>{claim.last_status_check ? formatDate(claim.last_status_check) : 'Never'}</span>
            </div>
            {claim.damage_description && (
              <div style={{ marginTop: 12, padding: '8px 10px', background: '#f8f9fa', borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>Damage Description</div>
                {claim.damage_description}
              </div>
            )}
            {claim.additional_notes && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: '#f8f9fa', borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>Additional Notes</div>
                {claim.additional_notes}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Detail from API */}
      {statusData && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h4 style={{ margin: 0, fontSize: 14 }}>UPS Capital Status Detail</h4></div>
          <div className="card-body" style={{ fontSize: 13 }}>
            {statusData.customerDisplayStatus && (
              <p><strong>Status:</strong> {statusData.customerDisplayStatus}</p>
            )}
            {statusData.claimAmount_Ext && (
              <p><strong>Claim Amount:</strong> {statusData.claimAmount_Ext}</p>
            )}
            {statusData.idrdocList?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong>Required Documents:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                  {statusData.idrdocList.map((doc, i) => <li key={i}>{doc}</li>)}
                </ul>
              </div>
            )}
            {statusData.checks?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong>Payments:</strong>
                {statusData.checks.map((check, i) => (
                  <div key={i} style={{ marginTop: 4, padding: '6px 10px', background: '#e6f4ea', borderRadius: 6 }}>
                    Check #{check.checkNumber} — ${check.grossAmount?.amount?.toFixed(2)} ({check.paymentMethod}) — {check.issueDate}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="card">
        <div className="card-header">
          <h4 style={{ margin: 0, fontSize: 14 }}>Claims Messages</h4>
        </div>
        <div className="card-body">
          {loadingMessages ? (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--gray-500)', fontSize: 13 }}>Loading messages...</div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--gray-500)', fontSize: 13 }}>No messages yet.</div>
          ) : (
            <div style={{ maxHeight: 300, overflow: 'auto', marginBottom: 12 }}>
              {messages.map((msg, i) => (
                <div key={msg.messageId || i} style={{
                  padding: '8px 12px', marginBottom: 8, borderRadius: 8,
                  background: msg.isAdjuster ? '#f0f4ff' : '#f8f9fa',
                  borderLeft: msg.isAdjuster ? '3px solid var(--navy)' : '3px solid var(--orange)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{msg.author}</span>
                    <span>{msg.time ? new Date(msg.time).toLocaleString() : ''}</span>
                  </div>
                  <div style={{ fontSize: 13 }}>{msg.body}</div>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8 }}>
            <input className="form-input" style={{ flex: 1 }} placeholder="Send a message to the claims adjuster..."
              value={newMessage} onChange={e => setNewMessage(e.target.value)} />
            <button type="submit" className="btn btn-primary" disabled={sendingMessage || !newMessage.trim()}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Send size={13} /> Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// =========== IMPORT CLAIMS MODAL ===========

// Parse rows from pasted portal table text
// Portal columns: Claim Number | File Date | Filed Claim Amount | Paid Amount |
// Check/Electronic Deposit Num | Check/Electronic Deposit Date | Tracking # / BOL |
// Reference # | UPS Account # | Receiver | Claim Status
function parsePortalRows(text) {
  const claimPattern = /\d{3}-\d{2}-\d{4,}/g;
  const allClaimNumbers = text.match(claimPattern) || [];
  if (allClaimNumbers.length === 0) return [];

  // Split into lines and try to find rows containing claim numbers
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const parsed = [];
  const seen = new Set();

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const claimMatch = line.match(/(\d{3}-\d{2}-\d{4,})/);
    if (!claimMatch) continue;
    const claimNumber = claimMatch[1];
    if (seen.has(claimNumber)) continue;
    seen.add(claimNumber);

    // Try to extract data from this line and surrounding lines
    // When pasting from portal, data might be on one line (tab-separated) or split across lines
    const chunk = lines.slice(i, i + 5).join('\t');

    // Extract date (MM/DD/YY or MM/DD/YYYY)
    const dateMatch = chunk.match(/(\d{2}\/\d{2}\/\d{2,4})/);
    const filedDate = dateMatch ? dateMatch[1] : null;

    // Extract dollar amounts (e.g. $278.29)
    const amounts = chunk.match(/\$[\d,]+\.\d{2}/g) || [];
    const claimAmount = amounts[0] ? parseFloat(amounts[0].replace(/[$,]/g, '')) : null;
    const paidAmount = amounts[1] ? parseFloat(amounts[1].replace(/[$,]/g, '')) : null;

    // Extract tracking number (1Z...)
    const trackingMatch = chunk.match(/(1Z[A-Z0-9]{10,})/i);
    const trackingNumber = trackingMatch ? trackingMatch[1] : null;

    // Extract reference number (just digits, 7 digits usually)
    const refMatch = chunk.match(/\b(20\d{4,5})\b/);
    const referenceNumber = refMatch ? refMatch[1] : null;

    // Extract status (at end of the row data)
    let status = null;
    const statusPatterns = ['Paid', 'Denied', 'Submitted', 'Claim Received', 'Documents Pending',
      'Investigation Pending', 'Submitted for Payment', 'Withdrawn', 'Closed'];
    for (const sp of statusPatterns) {
      if (chunk.includes(sp)) { status = sp; break; }
    }

    // Extract receiver name — typically after UPS account # (615R91) and before status
    let receiver = null;
    const receiverMatch = chunk.match(/615R91\s+([A-Za-z]+ [A-Za-z]+)/);
    if (receiverMatch) receiver = receiverMatch[1];
    // Fallback: look for "Paid to UPS" pattern to find paid date, then name after account#
    if (!receiver) {
      const nameMatch = chunk.match(/\b([A-Z][a-z]+ [A-Z][a-z]+)\s+(?:Paid|Denied|Submitted|Claim)/);
      if (nameMatch) receiver = nameMatch[1];
    }

    parsed.push({
      claimNumber,
      filedDate,
      claimAmount,
      paidAmount,
      trackingNumber,
      referenceNumber,
      receiver,
      status: status || 'Claim Received',
    });
  }

  // If line-by-line parsing didn't work well, fall back to just claim numbers
  if (parsed.length === 0) {
    const unique = [...new Set(allClaimNumbers)];
    return unique.map(cn => ({ claimNumber: cn, status: 'Claim Received' }));
  }

  return parsed;
}

function ImportClaimsModal({ onClose, onImported, existingClaimNumbers }) {
  const [pastedText, setPastedText] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const parsed = parsePortalRows(pastedText);
  const newClaims = parsed.filter(p => !existingClaimNumbers.includes(p.claimNumber));
  const alreadyImported = parsed.filter(p => existingClaimNumbers.includes(p.claimNumber));

  async function handleImport() {
    if (newClaims.length === 0) return;
    setImporting(true);
    setProgress({ current: 0, total: newClaims.length });
    const importResults = [];

    // Build a tracking-number-to-order lookup from Neon
    let ordersByTracking = {};
    try {
      const allOrders = await neonQuery('orders_open_claims');
      if (Array.isArray(allOrders)) {
        for (const o of allOrders) {
          if (o.tracking_number) ordersByTracking[o.tracking_number] = o;
        }
      }
    } catch (_) {}
    // Also try a broader lookup — recent orders with tracking numbers
    try {
      const recentOrders = await neonQuery('orders_recent_with_tracking');
      if (Array.isArray(recentOrders)) {
        for (const o of recentOrders) {
          if (o.tracking_number && !ordersByTracking[o.tracking_number]) {
            ordersByTracking[o.tracking_number] = o;
          }
        }
      }
    } catch (_) {}

    for (const cn of alreadyImported) {
      importResults.push({ claimNumber: cn.claimNumber, status: 'skipped', message: 'Already imported' });
    }

    for (let i = 0; i < newClaims.length; i++) {
      const row = newClaims[i];
      setProgress({ current: i + 1, total: newClaims.length });

      try {
        const upscStatus = row.status || 'Claim Received';
        const displayStatus = mapUpscStatus(upscStatus);
        const resolvedDate = ['Paid', 'Denied', 'Closed', 'Withdrawn'].includes(upscStatus)
          ? new Date().toISOString() : null;

        // Convert filed date MM/DD/YY to ISO
        let filedDateISO = null;
        if (row.filedDate) {
          const parts = row.filedDate.split('/');
          if (parts.length === 3) {
            let year = parts[2];
            if (year.length === 2) year = '20' + year;
            filedDateISO = `${year}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`;
          }
        }

        // Match tracking number to an order
        const matchedOrder = row.trackingNumber ? ordersByTracking[row.trackingNumber] : null;
        const orderId = matchedOrder ? String(matchedOrder.id) : null;
        const bcOrderId = matchedOrder ? String(matchedOrder.bc_order_id || '') : null;
        // Use receiver from portal, fall back to matched order customer name
        const customerName = row.receiver || (matchedOrder ? `${matchedOrder.first_name || ''} ${matchedOrder.last_name || ''}`.trim() : null);

        await neonQuery('claims_create', {
          claim_number: row.claimNumber,
          order_id: orderId,
          bc_order_id: bcOrderId,
          tracking_number: row.trackingNumber || null,
          customer_name: customerName,
          ship_date: filedDateISO,
          loss_date: filedDateISO,
          merchandise_amount: row.claimAmount || 0,
          shipping_amount: 0,
          claim_amount: row.claimAmount || 0,
          status: displayStatus,
          upsc_status: upscStatus,
          paid_amount: row.paidAmount || null,
          resolved_date: resolvedDate,
        });

        const amountInfo = row.claimAmount ? ` $${row.claimAmount.toFixed(2)}` : '';
        const paidInfo = row.paidAmount ? ` — paid $${row.paidAmount.toFixed(2)}` : '';
        const receiverInfo = row.receiver ? ` — ${row.receiver}` : '';
        importResults.push({ claimNumber: row.claimNumber, status: 'success', message: upscStatus + amountInfo + paidInfo + receiverInfo });
      } catch (e) {
        importResults.push({ claimNumber: row.claimNumber, status: 'error', message: e.message });
      }
    }

    setResults(importResults);
    setDone(true);
    setImporting(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget && !importing) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: 'var(--navy)' }}>Import Claims from UPS Capital</h3>
          {!importing && <button className="btn btn-ghost" onClick={onClose} style={{ padding: 4 }}><X size={18} /></button>}
        </div>

        {!done ? (
          <>
            <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 4 }}>
              Go to <a href="https://online.upscapital.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--orange)' }}>UPS Capital Portal</a> &rarr;
              View All Claims &rarr; select all the text on the page (Ctrl+A) &rarr; copy (Ctrl+C) &rarr; paste below.
            </p>
            <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
              Claim numbers (XXX-XX-XXXXXX) will be automatically extracted from whatever you paste.
            </p>
            <textarea
              className="form-textarea"
              rows={8}
              placeholder="Paste anything from the UPS Capital portal here — the full page, a table, individual claim numbers, whatever. Claim numbers will be extracted automatically."
              value={pastedText}
              onChange={e => setPastedText(e.target.value)}
              style={{ width: '100%', fontSize: 13 }}
              disabled={importing}
            />

            {pastedText.trim() && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#f8f9fa', borderRadius: 6, fontSize: 13 }}>
                {parsed.length === 0 ? (
                  <span style={{ color: '#c62828' }}>No claim numbers found. Looking for patterns like 000-07-370916.</span>
                ) : (
                  <>
                    <span style={{ fontWeight: 600 }}>Found {parsed.length} claim{parsed.length !== 1 ? 's' : ''}</span>
                    {alreadyImported.length > 0 && (
                      <span style={{ color: 'var(--gray-500)' }}> ({alreadyImported.length} already imported)</span>
                    )}
                    <span style={{ color: 'var(--gray-500)' }}> — {newClaims.length} new to import</span>
                    <div style={{ marginTop: 6, maxHeight: 200, overflow: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <th style={{ textAlign: 'left', padding: '2px 6px' }}>Claim #</th>
                            <th style={{ textAlign: 'left', padding: '2px 6px' }}>Date</th>
                            <th style={{ textAlign: 'right', padding: '2px 6px' }}>Amount</th>
                            <th style={{ textAlign: 'right', padding: '2px 6px' }}>Paid</th>
                            <th style={{ textAlign: 'left', padding: '2px 6px' }}>Receiver</th>
                            <th style={{ textAlign: 'left', padding: '2px 6px' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.map(r => (
                            <tr key={r.claimNumber} style={{
                              borderBottom: '1px solid #f0f0f0',
                              opacity: existingClaimNumbers.includes(r.claimNumber) ? 0.4 : 1,
                            }}>
                              <td style={{ padding: '2px 6px', fontFamily: 'monospace' }}>{r.claimNumber}</td>
                              <td style={{ padding: '2px 6px' }}>{r.filedDate || '—'}</td>
                              <td style={{ padding: '2px 6px', textAlign: 'right' }}>{r.claimAmount ? '$' + r.claimAmount.toFixed(2) : '—'}</td>
                              <td style={{ padding: '2px 6px', textAlign: 'right', color: r.paidAmount ? '#1e7e34' : '#999' }}>{r.paidAmount ? '$' + r.paidAmount.toFixed(2) : '—'}</td>
                              <td style={{ padding: '2px 6px' }}>{r.receiver || '—'}</td>
                              <td style={{ padding: '2px 6px' }}>{r.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {importing && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>
                  <span>Importing claim {progress.current} of {progress.total}...</span>
                  <span>{Math.round((progress.current / progress.total) * 100)}%</span>
                </div>
                <div style={{ height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', background: 'var(--orange)', borderRadius: 3,
                    width: `${(progress.current / progress.total) * 100}%`,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            )}

            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {!importing && <button className="btn btn-outline" onClick={onClose}>Cancel</button>}
              <button className="btn btn-primary" onClick={handleImport}
                disabled={importing || newClaims.length === 0}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {importing ? <RefreshCw size={13} className="spin" /> : <Download size={13} />}
                {importing ? `Importing ${progress.current}/${progress.total}...` : `Import ${newClaims.length} Claim${newClaims.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16, maxHeight: 400, overflow: 'auto' }}>
              {results.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                  borderBottom: '1px solid #f0f0f0', fontSize: 13,
                }}>
                  {r.status === 'success' ? <CheckCircle size={14} color="#1e7e34" /> :
                   r.status === 'skipped' ? <Clock size={14} color="#b8860b" /> :
                   <XCircle size={14} color="#c62828" />}
                  <span style={{ fontFamily: 'monospace', fontWeight: 600, minWidth: 130 }}>{r.claimNumber}</span>
                  <span style={{ color: 'var(--gray-500)', flex: 1, fontSize: 12 }}>{r.message}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                <strong style={{ color: '#1e7e34' }}>{results.filter(r => r.status === 'success').length}</strong> imported,
                {' '}<strong style={{ color: '#b8860b' }}>{results.filter(r => r.status === 'skipped').length}</strong> skipped,
                {' '}<strong style={{ color: '#c62828' }}>{results.filter(r => r.status === 'error').length}</strong> failed
              </span>
              <button className="btn btn-primary" onClick={() => { onImported(); onClose(); }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =========== MAIN COMPONENT ===========
export default function UpsClaims({ navParams, navigateTo }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [showNewClaim, setShowNewClaim] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [syncingProd, setSyncingProd] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [apiConnected, setApiConnected] = useState(null); // null = unknown, true/false
  const [prefillOrder, setPrefillOrder] = useState(null);

  // If navigated from Order Details with a prefill order, auto-open new claim modal
  useEffect(() => {
    if (navParams?.prefillOrder) {
      const o = navParams.prefillOrder;
      console.log('[Claims prefill] order:', { total: o.total, products: o.products?.map(p => ({ name: p.name, total: p.total, price: p.price })), lineItems: o.lineItems });
      // Extract shipping cost from lineItems (second item is usually shipping)
      const shipLine = o.lineItems?.find(li => li.label && !li.label.includes('Packing') && !li.label.includes('Adjustment') && !li.label.includes('Saturday') && !li.label.includes('Surcharge') && !li.label.includes('Coupon') && !li.label.includes('Gift'));
      const shipCost = shipLine ? parseFloat(shipLine.price?.replace(/[$,]/g, '') || '0') : 0;
      // Product total from individual product prices × quantities
      const productTotal = o.products?.reduce((sum, p) => {
        const price = parseFloat(String(p.price || '0').replace(/[$,]/g, ''));
        const qty = Number(p.qty || 1);
        const total = parseFloat(String(p.total || '0').replace(/[$,]/g, ''));
        return sum + (total > 0 ? total : price * qty);
      }, 0) || 0;
      // Order total as fallback
      const orderTotal = parseFloat(String(o.total || '0').replace(/[$,]/g, '')) || 0;
      // Also sum all line items (packing, shipping, adjustments, etc.)
      const lineItemTotal = o.lineItems?.reduce((sum, li) => sum + parseFloat(String(li.price || '0').replace(/[$,\-]/g, '')), 0) || 0;
      const merchAmount = productTotal > 0 ? productTotal : (orderTotal > 0 ? orderTotal : lineItemTotal);
      console.log('[Claims prefill] computed:', { shipCost, productTotal, orderTotal, merchAmount });

      setPrefillOrder({
        id: o.internalId || o.id,
        bc_order_id: o.id,
        tracking_number: o.trackingNumber || '',
        first_name: (o.customer?.shipping?.name || o.customer?.billing?.name || '').split(' ')[0],
        last_name: (o.customer?.shipping?.name || o.customer?.billing?.name || '').split(' ').slice(1).join(' '),
        email: o.customer?.billing?.email || o.customer?.shipping?.email || '',
        order_amount: merchAmount,
        shipping_cost: shipCost,
        order_date: o.orderDate || '',
        ship_date: o.shipDate || '',
        selected_delivery: o.selectedDelivery || '',
        actual_delivery: o.actualDelivery || '',
      });
      setShowNewClaim(true);
    }
  }, [navParams]);

  const loadClaims = useCallback(async () => {
    try {
      const data = await neonQuery('claims_list');
      setClaims(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load claims:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadClaims(); }, [loadClaims]);

  // Test API connection on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await insureshieldApi('test-auth');
        setApiConnected(res?.ok === true);
      } catch {
        setApiConnected(false);
      }
    })();
  }, []);

  // Refresh status for all open claims
  async function refreshAllStatuses() {
    const openClaims = claims.filter(c => !['Paid', 'Denied'].includes(c.status) && c.claim_number);
    if (openClaims.length === 0) return;
    setRefreshingAll(true);

    const updates = [];
    for (const claim of openClaims) {
      try {
        const data = await insureshieldApi('claim-status', { claimNumber: claim.claim_number });
        const upscStatus = data?.customerDisplayStatus;
        if (upscStatus) {
          const displayStatus = mapUpscStatus(upscStatus);
          const paidAmount = data?.checks?.reduce((sum, c) => sum + (c?.grossAmount?.amount || 0), 0) || null;
          const resolvedDate = ['Paid', 'Denied', 'Closed', 'Withdrawn'].includes(upscStatus)
            ? new Date().toISOString() : null;
          updates.push({
            claim_number: claim.claim_number,
            status: displayStatus,
            upsc_status: upscStatus,
            paid_amount: paidAmount,
            resolved_date: resolvedDate,
          });
        }
      } catch (e) {
        console.error('Failed to refresh status for', claim.claim_number, e);
      }
    }

    if (updates.length > 0) {
      await neonQuery('claims_bulk_update_status', { updates });
    }
    await loadClaims();
    setRefreshingAll(false);
  }

  // Mirror CPx to production: refresh EVERY claim from UPS Capital and remove any
  // that no longer exists there (404) — e.g. old UAT/test claims. Production is
  // the source of truth.
  async function syncFromProduction() {
    if (!window.confirm('Mirror CPx to production?\n\nThis refreshes every claim from UPS Capital InsureShield and REMOVES any claim that isn\'t in production (e.g. test claims). Production is the source of truth.')) return;
    setSyncingProd(true);
    setSyncMsg(null);
    const updates = [];
    const toDelete = [];
    for (const claim of claims) {
      if (!claim.claim_number) continue;
      try {
        const data = await insureshieldApi('claim-status', { claimNumber: claim.claim_number });
        if (data?.errors) { toDelete.push(claim.claim_number); continue; }  // not in production
        const upscStatus = data?.customerDisplayStatus;
        if (!upscStatus) continue;
        const paidAmount = data?.checks?.reduce((s, c) => s + (c?.grossAmount?.amount || 0), 0) || null;
        const issueDate = data?.checks?.[0]?.issueDate || null;
        const resolvedDate = issueDate || (['Paid', 'Denied', 'Closed', 'Withdrawn'].includes(upscStatus) ? new Date().toISOString().slice(0, 10) : null);
        updates.push({
          claim_number: claim.claim_number, status: mapUpscStatus(upscStatus),
          upsc_status: upscStatus, paid_amount: paidAmount, resolved_date: resolvedDate,
        });
      } catch (e) { /* transient network error — leave the claim alone, don't delete */ }
    }
    if (updates.length) await neonQuery('claims_bulk_update_status', { updates });
    for (const cn of toDelete) { try { await neonQuery('claims_delete', { claim_number: cn }); } catch (_) {} }
    await loadClaims();
    setSyncingProd(false);
    setSyncMsg(`Synced ${updates.length} claim${updates.length === 1 ? '' : 's'} from production${toDelete.length ? ` · removed ${toDelete.length} not in production` : ''}.`);
  }

  if (selectedClaim) {
    return (
      <div className="page-content">
        <ClaimDetail
          claim={selectedClaim}
          onBack={() => { setSelectedClaim(null); loadClaims(); }}
          onRefresh={loadClaims}
        />
      </div>
    );
  }

  const statuses = ['All', 'Paid', 'Under Review', 'Pending', 'Submitted', 'Denied'];
  const filtered = filter === 'All' ? claims : claims.filter(c => c.status === filter);

  // Summary stats
  const totalFiled = claims.length;
  const totalClaimValue = claims.reduce((s, c) => s + (parseFloat(c.claim_amount) || 0), 0);
  const totalPaid = claims.filter(c => c.status === 'Paid').reduce((s, c) => s + (parseFloat(c.paid_amount) || 0), 0);
  const openClaimsCount = claims.filter(c => !['Paid', 'Denied'].includes(c.status)).length;
  const avgResolution = (() => {
    const resolved = claims.filter(c => c.resolved_date && c.filed_date);
    if (!resolved.length) return '—';
    const days = resolved.map(c => {
      const f = new Date(c.filed_date);
      const r = new Date(c.resolved_date);
      return (r - f) / (1000 * 60 * 60 * 24);
    });
    return Math.round(days.reduce((a, b) => a + b, 0) / days.length) + ' days';
  })();

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--navy)' }}>UPS Capital Claims</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 13 }}>
            InsureShield claim tracking and filing
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={syncFromProduction} disabled={syncingProd}
            title="Mirror CPx to production: refresh every claim from UPS Capital and remove any not in production">
            <RefreshCw size={13} className={syncingProd ? 'spin' : ''} />
            {syncingProd ? 'Syncing…' : 'Sync from Production'}
          </button>
          <button className="btn btn-outline" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={refreshAllStatuses} disabled={refreshingAll || openClaimsCount === 0}>
            <RefreshCw size={13} className={refreshingAll ? 'spin' : ''} />
            {refreshingAll ? 'Refreshing...' : `Refresh Status (${openClaimsCount})`}
          </button>
          <button className="btn btn-outline" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={() => setShowImport(true)}>
            <Download size={13} /> Import Claims
          </button>
          <button className="btn btn-primary" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={() => setShowNewClaim(true)}>
            <Plus size={14} /> File New Claim
          </button>
          <a href="https://online.upscapital.com" target="_blank" rel="noopener noreferrer"
            className="btn btn-outline" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <ExternalLink size={14} /> UPS Capital Portal
          </a>
        </div>
      </div>

      {syncMsg && (
        <div style={{ marginBottom: 16, padding: '8px 14px', borderRadius: 8, background: '#e6f4ea', color: '#1a8a4a', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle size={14} /> {syncMsg}
          <button onClick={() => setSyncMsg(null)} className="btn btn-ghost" style={{ marginLeft: 'auto', padding: 2 }}><X size={14} /></button>
        </div>
      )}

      {/* API Connection Status */}
      {apiConnected === false && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid var(--orange)', background: '#fff8f0' }}>
          <div className="card-body" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <AlertTriangle size={18} color="var(--orange)" />
            <div style={{ fontSize: 13 }}>
              <strong>API connection failed.</strong> InsureShield credentials may be invalid or the UAT server may be down.
              Claims saved locally will still appear but status updates require API access.
            </div>
          </div>
        </div>
      )}

      {apiConnected === true && claims.length === 0 && !loading && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid #1e7e34', background: '#e6f4ea' }}>
          <div className="card-body" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircle size={18} color="#1e7e34" />
            <div style={{ fontSize: 13 }}>
              <strong>Connected to UPS Capital InsureShield API (UAT).</strong> No claims filed yet. Click "File New Claim" to get started.
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card">
          <div className="card-body" style={{ padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Claims Filed</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy)' }}>{totalFiled}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Total Claimed</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy)' }}>{formatCurrency(totalClaimValue)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Total Paid</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1e7e34' }}>{formatCurrency(totalPaid)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Open Claims</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#b8860b' }}>{openClaimsCount}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Avg Resolution</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy)' }}>{avgResolution}</div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {statuses.map(s => {
          const count = s === 'All' ? claims.length : claims.filter(c => c.status === s).length;
          return (
            <button
              key={s}
              className={filter === s ? 'btn btn-primary' : 'btn btn-outline'}
              style={{ fontSize: 12, padding: '5px 14px' }}
              onClick={() => setFilter(s)}
            >
              {s}
              {s !== 'All' && <span style={{ marginLeft: 4, opacity: 0.7 }}>({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Claims Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Loading claims...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>
              {claims.length === 0 ? 'No claims filed yet.' : 'No claims match this filter.'}
            </div>
          ) : (
            <table className="product-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Claim #</th>
                  <th>Order</th>
                  <th>Tracking</th>
                  <th>Customer</th>
                  <th>Reason</th>
                  <th style={{ textAlign: 'right' }}>Claimed</th>
                  <th style={{ textAlign: 'right' }}>Paid</th>
                  <th>Status</th>
                  <th>Filed</th>
                  <th>Resolved</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(claim => {
                  const lossCauseLabel = LOSS_CAUSES.find(lc => lc.value === claim.loss_cause)?.label || claim.loss_cause || '—';
                  return (
                    <tr key={claim.id || claim.claim_number} style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedClaim(claim)}>
                      <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{claim.claim_number}</td>
                      <td>{claim.bc_order_id || claim.order_id ? `#${claim.bc_order_id || claim.order_id}` : '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{claim.tracking_number || '—'}</td>
                      <td>{claim.customer_name || '—'}</td>
                      <td>{lossCauseLabel}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(claim.claim_amount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: claim.paid_amount ? '#1e7e34' : 'var(--gray-400)' }}>
                        {formatCurrency(claim.paid_amount)}
                      </td>
                      <td><StatusBadge status={claim.upsc_status || claim.status} /></td>
                      <td style={{ fontSize: 12 }}>{formatDate(claim.filed_date)}</td>
                      <td style={{ fontSize: 12 }}>{formatDate(claim.resolved_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showNewClaim && (
        <NewClaimModal
          onClose={() => { setShowNewClaim(false); setPrefillOrder(null); }}
          onSubmitted={loadClaims}
          prefillOrder={prefillOrder}
        />
      )}

      {showImport && (
        <ImportClaimsModal
          onClose={() => setShowImport(false)}
          onImported={loadClaims}
          existingClaimNumbers={claims.map(c => c.claim_number)}
        />
      )}
    </div>
  );
}
